import { BaseWebComponentController, WebComponentEvent } from '../../../../../framework/BaseWebComponentController';
import { IntegrityMonitorCustomRuleMainFormWebComponent } from '../component/IntegrityMonitorCustomRuleMainFormWebComponent';
import { IntegrityMonitorCustomRuleMainFormView } from '../view/IntegrityMonitorCustomRuleMainFormView';
import { template } from '../view/template';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { Rights } from '@nirvana/shared/constants';

export class IntegrityMonitorCustomRuleMainFormController extends BaseWebComponentController {
  public view!: IntegrityMonitorCustomRuleMainFormView;
  private currentEntityType: number | null;

  private subject: BehaviorSubject<boolean>;
  private valid$: Observable<boolean>;

  constructor(component: IntegrityMonitorCustomRuleMainFormWebComponent) {
    super(component);

    this.currentEntityType = null;
    this.subject = new BehaviorSubject<boolean>(true);
    this.valid$ = this.subject.pipe(distinctUntilChanged());

    this.initialSetup({
      viewConstructor: IntegrityMonitorCustomRuleMainFormView,
      viewTemplate: template,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$ruleEntityTypeFormField.Input,
        event: 'selectionChanged',
        callback: this.renderEntitySecuritySection,
      },
    ];
  }

  public createObservable(): Observable<boolean> {
    this.view.formGroup.valid$.subscribe(valid => {
      this.subject.next(valid && this.isCurrentEntityFormValid());
    });

    for (let index = 1; index < this.view.dom.$entitySpecificForms.length; index++) {
      const formComponent = this.view.dom.$entitySpecificForms[index];
      if (formComponent) {
        formComponent.valid$.subscribe(valid => {
          if (this.currentEntityType === index) {
            this.subject.next(valid && this.view.formGroup.valid);
          }
        });
      }
    }

    return this.valid$;
  }

  private renderEntitySecuritySection(event: WebComponentEvent) {
    const entityType: number = event.detail.value;
    if (entityType === null || entityType === 0 || entityType === undefined) return;

    if (this.currentEntityType) {
      const oldForm = this.view.dom.$entitySpecificForms[this.currentEntityType];
      if (oldForm) oldForm.hidden = true;
    }

    this.currentEntityType = entityType;
    const targetForm = this.view.dom.$entitySpecificForms[this.currentEntityType];
    if (targetForm) targetForm.hidden = false;

    if (this.subject) this.subject.next(this.view.formGroup.valid && this.isCurrentEntityFormValid());
  }

  public isValid(): boolean {
    return this.view.formGroup.valid && this.isCurrentEntityFormValid();
  }

  private isCurrentEntityFormValid(): boolean {
    if (this.currentEntityType === null || this.currentEntityType === 0) return false;

    const targetForm = this.view.dom.$entitySpecificForms[this.currentEntityType];
    if (!targetForm) return false;

    const valid = targetForm.valid;
    return valid !== undefined && valid;
  }

  public getValue() {
    return {
      general: this.view.formGroup.getValueRecursively(this.view.formGroup, true).general,
      security: this.getCurrentEntityFormValue(),
    };
  }

  private getCurrentEntityFormValue() {
    if (this.currentEntityType === null || this.currentEntityType === 0) return null;

    const targetForm = this.view.dom.$entitySpecificForms[this.currentEntityType];
    if (!targetForm) return null;

    return targetForm.value;
  }

  public resetForm() {
    // discard changes and skip validation
    this.view.formGroup.applyToFormGroupFields(field => {
      if (field instanceof GzFormFieldWebComponent) {
        field.discardChanges();
        field.resetField();
      }
    });

    for (const formComponent of this.view.dom.$entitySpecificForms.slice(1)) {
      if (formComponent) formComponent.reset();
    }

    if (this.currentEntityType) {
      const oldForm = this.view.dom.$entitySpecificForms[this.currentEntityType];
      if (oldForm) oldForm.hidden = true;
    }
    this.currentEntityType = null;
  }

  public setData(ruleData: any) {
    this.view.formGroup.value = ruleData;
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.view.formGroup.setDisabled(true);
    }
    this.currentEntityType = ruleData.general.entityType;
    if (this.currentEntityType !== null)
      this.view.dom.$entitySpecificForms[this.currentEntityType]?.setData(ruleData.security);
  }
}
